import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from "../../../config/AuthContext";
import { GetShopItems } from "../../../dao/jutge/GetShopItems";
import { PostBuyShopItem } from "../../../dao/jutge/PostBuyShopItem";
import { PutShopItemAula } from "../../../dao/jutge/PutShopItemAula";
import { PostCreateShopItem } from "../../../dao/jutge/PostCreateShopItem";
import { PutShopItem } from "../../../dao/jutge/PutShopItem";
import { GetAlumneId } from "../../../dao/GetAlumneId";
import { GetUsuarisIdJutgeNotes } from "../../../dao/GetUsuarisIdJutgeNotes";
import { getJavamonImage, GetSkins, GetJavamonConfig } from "../../../dao/jutge/GetJavamonConfig";
import { Button, Card, Col, message, Row, Statistic, Tag, Typography, Badge, Select, Switch, InputNumber, Skeleton, Tabs, Modal, Form, Input } from "antd";
import { ShoppingOutlined, WalletOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import UseWebSocketSubscription from "../../UseWebSocketSubscription";

const { Title, Text } = Typography;

interface ShopItem {
    id: number;
    nom: string;
    descr: string;
    cost: number;
    image: string;
    imagen?: string;
    type: string;
    stock: number;
    hidden?: number;
    is_new?: number;
    id_game_item?: number;
}

const EQUIPMENT_TYPES = ['skin', 'weapon', 'bow', 'shield'];

const TYPE_CONFIG: Record<string, { color: string; label: string; badge: string; badgeColor: string }> = {
    skin: { color: 'volcano-inverse', label: '👕 Skin', badge: 'SKIN', badgeColor: 'volcano' },
    weapon: { color: 'red-inverse', label: '⚔️ Arma', badge: 'ARMA', badgeColor: 'red' },
    bow: { color: 'cyan-inverse', label: '🏹 Arco', badge: 'ARCO', badgeColor: 'cyan' },
    shield: { color: 'blue-inverse', label: '🛡️ Escudo', badge: 'ESCUDO', badgeColor: 'blue' },
    ability: { color: 'purple-inverse', label: 'Habilidad', badge: 'ÉPICO', badgeColor: 'purple' },
    magic: { color: 'purple-inverse', label: '🪄 Magia', badge: 'ÉPICO', badgeColor: 'purple' },
    magia: { color: 'purple-inverse', label: '🪄 Magia', badge: 'ÉPICO', badgeColor: 'purple' },
    arrow: { color: 'cyan-inverse', label: '🎯 Flecha', badge: 'FLECHA', badgeColor: 'cyan' },
    flecha: { color: 'cyan-inverse', label: '🎯 Flecha', badge: 'FLECHA', badgeColor: 'cyan' },
    avatar: { color: 'gold-inverse', label: '👤 Avatar', badge: 'AVATAR', badgeColor: 'gold' },
    consumable: { color: 'geekblue-inverse', label: 'Consumible', badge: '', badgeColor: 'geekblue' },
    JAVAmon: { color: 'magenta-inverse', label: 'JAVAmon', badge: 'JAVAMON', badgeColor: 'magenta' },
    JAVAball: { color: 'orange-inverse', label: 'JAVAball', badge: 'JAVABALL', badgeColor: 'orange' },
};

const getTypeConfig = (type: string) =>
    TYPE_CONFIG[type] || { color: 'default', label: type, badge: '', badgeColor: 'default' };

// Tipos que un profesor puede crear en la tienda. 'listKey' indica de qué
// listado se cargan los elementos concretos; 'imageType' es el segmento usado
// por el endpoint /jutge/game/image/{type}/{id} para mostrar la imagen.
const CREATABLE_TYPES: { value: string; label: string; listKey: string; imageType: string }[] = [
    { value: 'skin', label: '👕 Skin', listKey: 'skins', imageType: 'skin' },
    { value: 'weapon', label: '⚔️ Arma', listKey: 'armes', imageType: 'arma' },
    { value: 'bow', label: '🏹 Arco', listKey: 'arcs', imageType: 'arc' },
    { value: 'arrow', label: '🎯 Flecha', listKey: 'flechas', imageType: 'flecha' },
    { value: 'shield', label: '🛡️ Escudo', listKey: 'escuts', imageType: 'escut' },
    { value: 'magia', label: '🪄 Magia', listKey: 'magias', imageType: 'magia' },
    { value: 'avatar', label: '👤 Avatar', listKey: 'avatars', imageType: 'avatar' },
    { value: 'JAVAmon', label: '🐛 JAVAmon', listKey: 'javamons', imageType: 'javamon' },
];

// Solo estos tipos (los gestionables) pueden crearse y editarse desde la tienda.
// Objetos fijos de la tienda como consumibles o de tipo 'Habilidad' (ability) no son editables.
const EDITABLE_TYPES = new Set([
    ...CREATABLE_TYPES.map(t => t.value),
    'magic',
    'arrow',
    'flecha'
]);

const Javashop = () => {
    const { userId, setBreadCrumb, user, alumne } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();

    // Si la ruta es /game/javashop, solo se puede acceder desde dentro del frame del juego
    useEffect(() => {
        if (location.pathname.startsWith('/game')) {
            const isInsideIframe = window.self !== window.top;
            if (!isInsideIframe) {
                navigate('/home', { replace: true });
            }
        }
    }, [location.pathname, navigate]);

    const [items, setItems] = useState<ShopItem[]>([]);
    const [points, setPoints] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [buying, setBuying] = useState<boolean>(false);
    const [currentBuyingId, setCurrentBuyingId] = useState<number | null>(null);
    const [updatingItemId, setUpdatingItemId] = useState<number | null>(null);
    const [selectedAula, setSelectedAula] = useState<string>('');
    const [aulas, setAulas] = useState<{ id: string; nom: string }[]>([]);
    const [activeTab, setActiveTab] = useState<string>('all');

    const isAdminUser = user.tipus === "0" || user.tipus === "1";
    const [adminMode, setAdminMode] = useState<boolean>(isAdminUser);

    // --- Creación / edición de objetos de tienda (admin) ---
    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [createForm] = Form.useForm();
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [gameLists, setGameLists] = useState<Record<string, any[]>>({});
    const [loadingLists, setLoadingLists] = useState<boolean>(false);
    const [editingItem, setEditingItem] = useState<ShopItem | null>(null);

    useEffect(() => {
        setAdminMode(isAdminUser);
    }, [isAdminUser]);

    useEffect(() => {
        if (typeof setBreadCrumb === 'function') {
            setBreadCrumb([
                { nom: 'Home', nav: '/home' },
                { nom: 'Perfil', nav: '/home/perfil' },
                { nom: 'JAVATienda', nav: '/home/javashop' }
            ], true);
        }

        if (isAdminUser) {
            fetchClassrooms();
        } else {
            GetAlumneId(userId).then(al => {
                if (al?.aules && al.aules.length > 0) {
                    const id = al.aules[0].id || al.aules[0].id_aula;
                    if (id) setSelectedAula(id.toString());
                }
            });
        }
    }, [user, alumne]);

    const fetchClassrooms = async () => {
        try {
            const data: any[] = await GetUsuarisIdJutgeNotes(userId);
            const uniqueAulasMap = new Map();
            data.flatMap(student => student.practiques || []).forEach((p: any) => {
                if (p.id_aula && p.aula) uniqueAulasMap.set(p.id_aula.toString(), p.aula);
            });
            const aulasList = Array.from(uniqueAulasMap.entries()).map(([id, nom]) => ({ id, nom }));
            setAulas(aulasList);
            if (aulasList.length > 0) setSelectedAula(aulasList[0].id);
        } catch (error) {
            console.error(error);
            message.error("Error cargando aulas");
        }
    };

    useEffect(() => {
        if (selectedAula) loadData();
    }, [selectedAula]);

    const onWebSocketMessage = useCallback(() => {
        if (selectedAula) loadData(false);
    }, [selectedAula]);

    UseWebSocketSubscription(['/topic/shop'], onWebSocketMessage);

    const loadData = async (showLoading = true) => {
        if (!selectedAula) return;
        if (showLoading) setLoading(true);
        try {
            const [shopItemsResponse, userResponse] = await Promise.all([
                GetShopItems(selectedAula),
                GetAlumneId(userId)
            ]);
            setItems(Array.isArray(shopItemsResponse) ? shopItemsResponse : []);
            const userPoints = userResponse?.points?.reduce((acc: number, p: any) => acc + (p.punts ?? 0), 0) || 0;
            setPoints(userPoints);
        } catch (error) {
            console.error("Error loading shop data:", error);
            message.error("Error loading shop data");
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleBuy = async (item: ShopItem) => {
        if (buying || !selectedAula) return;
        if (points < item.cost) { message.error("No tienes suficientes puntos!"); return; }
        setBuying(true);
        setCurrentBuyingId(item.id);
        try {
            const res = await PostBuyShopItem(userId, item.id.toString(), selectedAula);
            if (res.ok) {
                message.success("¡Compra realizada con éxito!");
                loadData(false);
            } else {
                message.error(res.msg || "Error al comprar");
                loadData(false);
            }
        } catch (error) {
            message.error("Error de conexión");
        } finally {
            setBuying(false);
            setCurrentBuyingId(null);
        }
    };

    const handleUpdate = async (item: ShopItem, stock: number, hidden: number, isNew: number) => {
        if (!selectedAula) return;
        setUpdatingItemId(item.id);
        try {
            const res = await PutShopItemAula(selectedAula, item.id.toString(), stock, hidden, isNew);
            if (res.ok) {
                message.success("Item actualizado");
                await loadData(false);
            } else {
                message.error("Error al actualizar: " + res.msg);
                await loadData(false);
            }
        } finally {
            setUpdatingItemId(null);
        }
    };

    // Carga (una sola vez) todos los listados de elementos de juego para poder
    // elegir el objeto concreto al crear un item de tienda.
    const ensureGameListsLoaded = async () => {
        if (Object.keys(gameLists).length > 0) return;
        setLoadingLists(true);
        try {
            const [skinsData, configData] = await Promise.all([GetSkins(), GetJavamonConfig()]);
            const lists: Record<string, any[]> = {
                skins: skinsData?.skins || [],
                armes: skinsData?.armes || [],
                escuts: skinsData?.escuts || [],
                arcs: skinsData?.arcs || [],
                flechas: skinsData?.flechas || [],
                magias: skinsData?.magias || [],
                avatars: skinsData?.avatars || [],
                javamons: configData?.javamons ? JSON.parse(configData.javamons) : []
            };
            setGameLists(lists);
        } catch (e) {
            console.error(e);
            message.error("Error cargando los listados de objetos");
        } finally {
            setLoadingLists(false);
        }
    };

    const openCreateModal = () => {
        createForm.resetFields();
        setSelectedType(null);
        setEditingItem(null);
        setCreateModalOpen(true);
        ensureGameListsLoaded();
    };

    const openEditModal = (item: ShopItem) => {
        setEditingItem(item);
        let normalizedType = item.type;
        if (normalizedType === 'magic' || normalizedType === 'ability') normalizedType = 'magia';
        if (normalizedType === 'arrow') normalizedType = 'arrow'; // or 'flecha' if matched
        setSelectedType(normalizedType);
        setCreateModalOpen(true);
        ensureGameListsLoaded();
        createForm.setFieldsValue({
            type: normalizedType,
            idGameItem: item.id_game_item ?? undefined,
            nom: item.nom,
            descr: item.descr,
            cost: item.cost,
            stock: item.stock
        });
    };

    // Cuando se elige un objeto concreto, autocompletamos el nombre/descr si están vacíos.
    const handleGameItemChange = (id: number) => {
        const cfg = CREATABLE_TYPES.find(t => t.value === selectedType);
        if (!cfg) return;
        const list = gameLists[cfg.listKey] || [];
        const selected = list.find((x: any) => x.id === id);
        if (!selected) return;
        const current = createForm.getFieldsValue();
        const patch: any = {};
        if (!current.nom) patch.nom = selected.nom;
        if (!current.descr && selected.descr) patch.descr = selected.descr;
        if (Object.keys(patch).length > 0) createForm.setFieldsValue(patch);
    };

    const handleSubmit = async () => {
        if (!selectedAula) {
            message.error("Selecciona un aula primero");
            return;
        }
        try {
            const values = await createForm.validateFields();
            setCreating(true);
            let sendType = values.type;
            if (sendType === 'magic') sendType = 'magia';
            if (sendType === 'arrow') sendType = 'arrow';

            const payload = {
                idAula: selectedAula,
                nom: values.nom,
                descr: values.descr || '',
                cost: values.cost ?? 0,
                stock: values.stock ?? 0,
                type: sendType,
                idGameItem: values.idGameItem ?? null
            };

            const res = editingItem
                ? await PutShopItem(editingItem.id.toString(), payload)
                : await PostCreateShopItem(payload);

            if (res.ok) {
                message.success(editingItem ? "Objeto actualizado" : "Objeto creado y añadido al aula");
                setCreateModalOpen(false);
                createForm.resetFields();
                setSelectedType(null);
                setEditingItem(null);
                await loadData(false);
            } else {
                message.error(res.msg || (editingItem ? "Error al actualizar el objeto" : "Error al crear el objeto"));
            }
        } catch (e: any) {
            if (e?.errorFields) return; // errores de validación del form
            message.error(editingItem ? "Error al actualizar el objeto" : "Error al crear el objeto");
        } finally {
            setCreating(false);
        }
    };

    const currentTypeCfg = CREATABLE_TYPES.find(t => t.value === selectedType);
    const currentList = currentTypeCfg ? (gameLists[currentTypeCfg.listKey] || []) : [];

    return (
        <div style={{ padding: '24px', width: '100%', background: '#f5f7fa', minHeight: 'calc(100vh - 64px)' }}>
            {/* Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '40px',
                background: 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                padding: '28px 32px', borderRadius: '16px',
                boxShadow: '0 8px 20px rgba(24, 144, 255, 0.2)', width: '100%'
            }}>
                <div>
                    <Title level={2} style={{ margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ShoppingOutlined /> JAVATienda
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }}>
                        ¡Gasta tus puntos de ranking y mejora tu experiencia!
                    </Text>
                    {isAdminUser && (
                        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Select value={selectedAula} onChange={(val) => setSelectedAula(val)} style={{ width: 250 }} disabled={aulas.length <= 1}>
                                {aulas.map((a) => <Select.Option key={a.id} value={a.id}>{a.nom}</Select.Option>)}
                            </Select>
                            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>Modo Admin</span>
                                <Switch checked={adminMode} onChange={setAdminMode} />
                            </div>
                            <Button
                                icon={<PlusOutlined />}
                                onClick={openCreateModal}
                                disabled={!selectedAula}
                                style={{ fontWeight: 'bold' }}
                            >
                                Crear objeto
                            </Button>
                        </div>
                    )}
                </div>
                <Card
                    bodyStyle={{ padding: '16px 24px' }}
                    style={{ borderRadius: '12px', minWidth: '220px', border: 'none', background: 'rgba(255,255,255,0.95)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                >
                    <Statistic
                        title={<span style={{ color: '#d48806', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase' }}>Tus Puntos</span>}
                        value={points}
                        prefix={<WalletOutlined style={{ color: '#d48806' }} />}
                        valueStyle={{ color: '#d48806', fontWeight: 'bold' }}
                    />
                </Card>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="large"
                style={{ marginBottom: '24px' }}
                items={[
                    { key: 'all', label: 'Todos los Objetos' },
                    { key: 'personaje', label: '👕 Personaje' },
                    { key: 'armas', label: '⚔️ Armas y Escudos' },
                    { key: 'magia', label: '🪄 Magias' },
                    { key: 'otros', label: '📦 Otros' },
                ]}
            />

            {/* Items grid */}
            <Row gutter={[24, 32]}>
                {loading ? (
                    Array.from({ length: 8 }).map((_, index) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={`skeleton-${index}`}>
                            <Card
                                style={{ borderRadius: '16px', overflow: 'hidden', height: '450px', border: '1px solid #f0f0f0' }}
                                bodyStyle={{ padding: '20px', display: 'flex', flexDirection: 'column' }}
                                cover={
                                    <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                        <Skeleton.Image style={{ width: 120, height: 120 }} active />
                                    </div>
                                }
                            >
                                <Skeleton active paragraph={{ rows: 3 }} title={{ width: '70%' }} />
                                <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
                                    <Skeleton.Button active block shape="round" style={{ height: '46px' }} />
                                </div>
                            </Card>
                        </Col>
                    ))
                ) : (
                    items
                        .filter(item => {
                            if (!adminMode && item.hidden !== 0) return false;

                            const t = item.type;
                            if (activeTab === 'all') return true;
                            if (activeTab === 'personaje') return ['skin', 'avatar'].includes(t);
                            if (activeTab === 'armas') return ['weapon', 'shield', 'bow', 'arrow', 'flecha'].includes(t);
                            if (activeTab === 'magia') return ['ability', 'magic', 'magia'].includes(t);
                            if (activeTab === 'otros') return !['skin', 'avatar', 'weapon', 'shield', 'bow', 'arrow', 'flecha', 'ability', 'magic', 'magia'].includes(t);
                            return true;
                        })
                        .map(item => {
                            const tc = getTypeConfig(item.type);
                            const isEquipment = EQUIPMENT_TYPES.includes(item.type);
                            const ribbonText = item.stock <= 0 ? "AGOTADO" : (item.is_new === 1 ? "NUEVO" : tc.badge || "");
                            const ribbonColor = item.stock <= 0 ? "red" : (item.is_new === 1 ? "cyan" : tc.badgeColor);
                            const showRibbon = item.stock <= 0 || item.is_new === 1 || !!tc.badge;

                            return (
                                <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                                    <Badge.Ribbon
                                        text={ribbonText}
                                        color={ribbonColor}
                                        style={{ display: showRibbon ? 'block' : 'none', top: 10 }}
                                    >
                                        <Card
                                            hoverable
                                            loading={updatingItemId === item.id}
                                            style={{
                                                height: '100%', borderRadius: '16px', overflow: 'hidden',
                                                border: isEquipment ? '1px solid #ffd591' : '1px solid #f0f0f0',
                                                boxShadow: item.stock <= 0 ? 'none' : (isEquipment ? '0 10px 20px rgba(250,140,22,0.12)' : '0 10px 20px rgba(0,0,0,0.06)'),
                                                transition: 'all 0.3s cubic-bezier(0.25,0.8,0.25,1)',
                                                filter: item.stock <= 0 ? 'grayscale(0.6) opacity(0.8)' : 'none'
                                            }}
                                            bodyStyle={{ padding: '20px', display: 'flex', flexDirection: 'column' }}
                                            cover={
                                                <div style={{
                                                    height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    background: isEquipment
                                                        ? 'radial-gradient(circle at center, #fff7e6 0%, #fff1b8 100%)'
                                                        : 'radial-gradient(circle at center, #ffffff 0%, #f5f7fa 100%)',
                                                    padding: '20px', borderBottom: '1px solid #f0f0f0'
                                                }}>
                                                    <img
                                                        alt={item.nom}
                                                        src={getJavamonImage(item)}
                                                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))', transition: 'transform 0.3s' }}
                                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                    />
                                                </div>
                                            }
                                            actions={[
                                                <Button
                                                    type="primary"
                                                    block
                                                    disabled={points < item.cost || buying || item.stock <= 0}
                                                    loading={buying && currentBuyingId === item.id}
                                                    onClick={() => handleBuy(item)}
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: item.stock <= 0 ? '#ff4d4f' : (points >= item.cost ? '#52c41a' : '#d9d9d9'),
                                                        color: 'white', fontWeight: 800, fontSize: '15px',
                                                        height: '46px', borderRadius: '8px',
                                                        margin: '0 16px 16px 16px', width: 'calc(100% - 32px)',
                                                        textTransform: 'uppercase',
                                                        boxShadow: (points >= item.cost && item.stock > 0) ? '0 4px 10px rgba(82,196,26,0.3)' : 'none'
                                                    }}
                                                >
                                                    {item.stock <= 0 ? "Agotado" : (buying && currentBuyingId === item.id ? "Procesando..." : "Comprar · " + item.cost + " pts")}
                                                </Button>
                                            ]}
                                        >
                                            <Card.Meta
                                                title={<span style={{ fontSize: '18px', fontWeight: 800, color: '#1f1f1f', whiteSpace: 'normal', lineHeight: '1.2' }}>{item.nom}</span>}
                                                description={
                                                    <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <Tag color={tc.color} style={{ margin: 0, fontWeight: 600, borderRadius: '4px' }}>{tc.label}</Tag>
                                                            <span style={{ fontSize: '13px', fontWeight: 700, color: item.stock > 0 ? '#1890ff' : '#ff4d4f', background: item.stock > 0 ? '#e6f7ff' : '#fff1f0', padding: '2px 8px', borderRadius: '12px' }}>
                                                                {item.stock > 0 ? item.stock + " disp." : "Sin Stock"}
                                                            </span>
                                                        </div>
                                                        <Text style={{ minHeight: '60px', color: '#595959', fontSize: '14px', lineHeight: '1.5' }}>{item.descr}</Text>
                                                        {adminMode && (
                                                            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                                                                {EDITABLE_TYPES.has(item.type) && (
                                                                    <Button
                                                                        icon={<EditOutlined />}
                                                                        size="small"
                                                                        block
                                                                        onClick={() => openEditModal(item)}
                                                                    >
                                                                        Editar objeto
                                                                    </Button>
                                                                )}
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Text strong>Stock:</Text>
                                                                    <InputNumber min={0} value={item.stock} onChange={(val) => handleUpdate(item, val || 0, item.hidden || 0, item.is_new || 0)} />
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Text strong>Visibilidad:</Text>
                                                                    <Switch checked={item.hidden === 0} onChange={(val) => handleUpdate(item, item.stock, val ? 0 : 1, item.is_new || 0)} checkedChildren="Visible" unCheckedChildren="Oculto" />
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Text strong>Novedad:</Text>
                                                                    <Switch checked={item.is_new === 1} onChange={(val) => handleUpdate(item, item.stock, item.hidden || 0, val ? 1 : 0)} checkedChildren="Nuevo" unCheckedChildren="Normal" />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                }
                                            />
                                        </Card>
                                    </Badge.Ribbon>
                                </Col>
                            );
                        })
                )}
            </Row>

            {/* Modal creación de objeto de tienda (solo admin) */}
            <Modal
                title={editingItem ? "Editar objeto de tienda" : "Crear objeto de tienda"}
                open={createModalOpen}
                onCancel={() => setCreateModalOpen(false)}
                onOk={handleSubmit}
                okText={editingItem ? "Guardar" : "Crear"}
                confirmLoading={creating}
                destroyOnClose
                width={600}
            >
                <div style={{ marginBottom: 12, color: '#8c8c8c' }}>
                    {editingItem
                        ? 'Los cambios se aplican al objeto global; el stock se ajusta para el aula seleccionada'
                        : 'El objeto se añadirá al aula seleccionada'}
                    {aulas.find(a => a.id === selectedAula)?.nom
                        ? ` (${aulas.find(a => a.id === selectedAula)?.nom})`
                        : ''}.
                </div>
                <Form form={createForm} layout="vertical" initialValues={{ cost: 100, stock: 10 }}>
                    <Form.Item
                        name="type"
                        label="Tipo de objeto"
                        rules={[{ required: true, message: 'Selecciona un tipo' }]}
                    >
                        <Select
                            placeholder="Selecciona el tipo"
                            onChange={(val) => {
                                setSelectedType(val);
                                createForm.setFieldsValue({ idGameItem: undefined });
                            }}
                            options={CREATABLE_TYPES.map(t => ({ value: t.value, label: t.label }))}
                        />
                    </Form.Item>

                    {selectedType && (
                        <Form.Item
                            name="idGameItem"
                            label="Elemento concreto"
                            rules={[{ required: true, message: 'Selecciona el elemento' }]}
                        >
                            <Select
                                placeholder={loadingLists ? "Cargando..." : "Selecciona el elemento"}
                                loading={loadingLists}
                                showSearch
                                optionFilterProp="label"
                                onChange={handleGameItemChange}
                                notFoundContent={loadingLists ? "Cargando..." : "No hay elementos"}
                                options={currentList.map((it: any) => ({
                                    value: it.id,
                                    label: `${it.nom || 'Sin nombre'} (#${it.id})`
                                }))}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="nom"
                        label="Nombre"
                        rules={[{ required: true, message: 'Introduce un nombre' }]}
                    >
                        <Input placeholder="Nombre visible en la tienda" />
                    </Form.Item>

                    <Form.Item name="descr" label="Descripción">
                        <Input.TextArea rows={3} placeholder="Descripción del objeto" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item
                            name="cost"
                            label="Coste (puntos)"
                            rules={[{ required: true, message: 'Introduce el coste' }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="stock"
                            label="Stock inicial"
                            rules={[{ required: true, message: 'Introduce el stock' }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </div>

                    {selectedType === 'JAVAmon' && (
                        <div style={{ color: '#d46b08', fontSize: 13 }}>
                            Para JAVAmon, al comprarse se busca por nombre; asegúrate de que el nombre
                            coincide con el del JAVAmon seleccionado.
                        </div>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default Javashop;
